import { Injectable } from '@angular/core';
import { v4 as uuid4 } from "uuid";
import { AuctionFilter } from '../models/auction-filter';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { DistributorAuction } from '../models/distributor-auction';
import { ContractorBaseAuction } from '../models/contractor-base-auction';
import { DistributorType } from '../models/distributor-type';
import { LoggingService } from '../../../services/logging.service';


const MODULE_PATH = "TakomaClient.app.modules.home.services.bus-auction-filter";


@Injectable()
export class BusAuctionFilterService {

  private messageFilter = new BehaviorSubject<AuctionFilter>(new AuctionFilter());
  private messageAuctionNumber = new BehaviorSubject<string>("<InitialValue>");
  private messageClearModalFilter = new BehaviorSubject<string>('');
  private messageDistributerAuction = new BehaviorSubject<DistributorAuction>(new DistributorAuction());
  private messageClearSearchModalFields = new BehaviorSubject<string>('');
  private messageContractBaseAuction = new BehaviorSubject<ContractorBaseAuction>(new ContractorBaseAuction());

  public currentStateAuctionFilter = new AuctionFilter();
  private currentStateDistributerAuction = new DistributorAuction();

  currentFilter$ = this.messageFilter.asObservable();
  currentAuctionNumber$ = this.messageAuctionNumber.asObservable();
  currentClearModalFilter$ = this.messageClearModalFilter.asObservable();
  currentDistributerAuction$ = this.messageDistributerAuction.asObservable();
  currentClearSearchModalFields$ = this.messageClearSearchModalFields.asObservable();
  currentContractBaseAuction$ = this.messageContractBaseAuction.asObservable();

  currentDistributorType = new DistributorType();

  currentAuction;

  constructor(private _loggingService: LoggingService) { }

  changeFilter(filter: AuctionFilter, isClear = false): void {
    const xid = uuid4();
    const qualName = `${MODULE_PATH}.BusAuctionFilterService.changeFilter`;
    const inputParams = { "filter": filter, "isClear": isClear };
    const description = "[MessageSender] Изменение фильтра таблицы аукционов";
    
    try {
      this._loggingService.logStart(xid, qualName, inputParams, description);
      
      if (!isClear) {
        Object.keys(filter).forEach(key => {
          this.currentStateAuctionFilter[key] = filter[key];
        });
      } else {
        Object.keys(filter).forEach(key => {
          if (filter[key] == null || filter[key].length === 0) {
            this.currentStateAuctionFilter[key] = null;
          }
        });
      }

      this.messageFilter.next(this.currentStateAuctionFilter);
      
      this._loggingService.logFinish(xid, qualName, inputParams, description);
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, inputParams, description);
      throw error;
    }
  }

  refreshAuctionTable(): void {
    const xid = uuid4();
    const qualName = `${MODULE_PATH}.BusAuctionFilterService.refreshAuctionTable`;
    const inputParams = {};
    const description = "[MessageSender] Обновление таблицы аукционов";

    try {
      this._loggingService.logStart(xid, qualName, inputParams, description);

      this.messageFilter.next(this.currentStateAuctionFilter);

      this._loggingService.logFinish(xid, qualName, inputParams, description);
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, inputParams, description);
      throw error;
    }
  }

  changeAuctionNumber(auctionNumber: string) {
    const xid = this._loggingService.generateUUID();
    const qualName = "app.modules.home.services.BusAuctionFilterService.changeAuctionNumber";
    const inputParams = { "auctionNumber": auctionNumber };
    const description = "Изменение номера аукциона";

    try {
      this._loggingService.logStart(xid, qualName, inputParams, description);

      this.currentStateDistributerAuction = new DistributorAuction();
      this.messageAuctionNumber.next(auctionNumber);

      this._loggingService.logFinish(xid, qualName, inputParams, description);
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, inputParams, description);
      throw error;
    }
  }

  changeClearModalFilter(key: string) {
    const xid = this._loggingService.generateUUID();
    const qualName = 'App.Services.BusAuctionFilterService.changeClearModalFilter';
    
    try {
      this._loggingService.logStart(xid, qualName, null, 'Clearing modal filter');
      
      this.messageClearModalFilter.next(key);
      
      this._loggingService.logFinish(xid, qualName, null, 'Modal filter cleared');
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, null, 'Failed to clear modal filter');
      throw error;
    }
  }

  changeClearSearchModalFields(key: string) {
    const xid = this._loggingService.generateUUID();
    const qualName = 'App.Services.BusAuctionFilterService.changeClearSearchModalFields';
    
    try {
      this._loggingService.logStart(xid, qualName, null, 'Clearing search modal fields');
      
      this.messageClearSearchModalFields.next(key);
      
      this._loggingService.logFinish(xid, qualName, null, 'Search modal fields cleared');
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, null, 'Failed to clear search modal fields');
      throw error;
    }
  }

  changeDistributerAuction(distributorAuction: DistributorAuction) {
    const xid = this._loggingService.generateUUID();
    const qualName = "app.modules.home.services.BusAuctionFilterService.changeDistributerAuction";
    const inputParams = {"distributorAuction": distributorAuction};
    
    try {
      this._loggingService.logStart(xid, qualName, inputParams, 'Changing distributer auction');

      if (distributorAuction != null) {
        Object.keys(distributorAuction).forEach(
          key => {
            this.currentStateDistributerAuction[key] = distributorAuction[key];
          }
        );
      }
      this.messageDistributerAuction.next(this.currentStateDistributerAuction);
      
      this._loggingService.logFinish(xid, qualName, inputParams, 'Distributer auction changed');
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, inputParams, 'Failed to change distributer auction');
      throw error;
    }
  }

  changeContractBaseAuction(contractBaseAuction: ContractorBaseAuction) {
    const xid = this._loggingService.generateUUID();
    const qualName = 'App.Services.BusAuctionFilterService.changeContractBaseAuction';
    
    try {
      this._loggingService.logStart(xid, qualName, 
        null,
        'Changing contract base auction');
      
      this.messageContractBaseAuction.next(contractBaseAuction);
      
      this._loggingService.logFinish(xid, qualName, null, 'Contract base auction changed');
    } catch (error) {
      this._loggingService.logError(xid, qualName, error, null, 'Failed to change contract base auction');
      throw error;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/home/services/bus-auction-filter.service.ts